import { useNavigate, useLocation, Link } from "react-router-dom";
import { LayoutDashboard, Users, PhoneCall, LogOut, Building2, KanbanSquare, GraduationCap, Bot, Inbox, ClipboardList, Tags, ReceiptText, Wallet, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { NotificationBell } from "@/components/NotificationBell";

const roleLabels = {
  admin_cabang: { text: "Admin Cabang", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  kepala_cabang: { text: "Kepala Cabang", cls: "bg-amber-50 text-amber-800 border-amber-300" },
  super_admin: { text: "Super Admin", cls: "bg-slate-100 text-slate-700 border-slate-300" },
};

const branchNav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/prospek", label: "Prospek", icon: Users, testid: "nav-prospek" },
  { to: "/pipeline", label: "Pipeline", icon: KanbanSquare, testid: "nav-pipeline" },
  { to: "/siswa-klien", label: "Siswa & Klien", icon: GraduationCap, testid: "nav-siswa-klien" },
  { to: "/pendaftaran", label: "Pendaftaran", icon: ClipboardList, testid: "nav-pendaftaran" },
  { to: "/master-program", label: "Program & Biaya", icon: Tags, testid: "nav-master-program" },
  { to: "/tagihan", label: "Tagihan", icon: ReceiptText, testid: "nav-tagihan" },
  { to: "/pembayaran", label: "Pembayaran", icon: Wallet, testid: "nav-pembayaran" },
  { to: "/laporan", label: "Laporan Keuangan", icon: BarChart3, testid: "nav-laporan" },
  { to: "/followup", label: "Follow-up", icon: PhoneCall, testid: "nav-followup" },
  { to: "/chatbot-settings", label: "Pengaturan Chatbot", icon: Bot, testid: "nav-chatbot-settings" },
  { to: "/inbox", label: "Inbox Chatbot", icon: Inbox, testid: "nav-inbox" },
];

const superAdminNav = [
  { to: "/cabang", label: "Kelola Cabang", icon: Building2, testid: "nav-cabang" },
];

export function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const r = roleLabels[user?.role] || { text: user?.role, cls: "bg-slate-100 text-slate-700 border-slate-300" };
  const nav = user?.role === "super_admin" ? superAdminNav : branchNav;
  const doLogout = async () => {
    await logout();
    toast.success("Anda telah keluar.");
    navigate("/login", { replace: true });
  };
  return (
    <div className="min-h-screen flex flex-col bg-slate-50 text-slate-900">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/90 backdrop-blur-md" data-testid="app-header">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-[#0f2847] grid place-items-center font-bold text-white text-sm">IEC</div>
            <div className="leading-tight hidden sm:block">
              <p className="font-bold text-sm text-[#0f2847]">IEC Mustikajaya</p>
              <p className="text-[11px] text-slate-500">Sistem CRM Cabang</p>
            </div>
          </div>
          <div className="flex items-center gap-2 min-w-0 flex-1 justify-end">
          <nav className="flex items-center gap-1 overflow-x-auto min-w-0 flex-nowrap">
            {nav.map((n) => {
              const A = n.icon;
              const active = loc.pathname === n.to || (n.to !== "/dashboard" && loc.pathname.startsWith(n.to));
              return (
                <Link key={n.to} to={n.to} data-testid={n.testid}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors shrink-0 ${active ? "bg-[#0f2847] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                  <A className="h-4 w-4" /><span className="hidden md:inline">{n.label}</span>
                </Link>
              );
            })}
            <button onClick={doLogout} data-testid="nav-logout"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-white bg-[#f97316] hover:bg-[#ea580c] ml-1 shrink-0">
              <LogOut className="h-4 w-4" /><span className="hidden md:inline">Keluar</span>
            </button>
          </nav>
          <NotificationBell />
          </div>
        </div>
      </header>
      <div className="border-b border-slate-100 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2 flex items-center justify-between text-xs text-slate-500">
          <span data-testid="layout-branch" className="inline-flex items-center gap-1.5">
            <Building2 className="h-3 w-3" /> Cabang: {user?.branch_name || "-"}
          </span>
          <span className="inline-flex items-center gap-2">
            <span data-testid="layout-user-name" className="font-medium text-slate-700">{user?.name}</span>
            <span className={`px-2 py-0.5 rounded-full border ${r.cls}`} data-testid="layout-role-badge">{r.text}</span>
          </span>
        </div>
      </div>
      <main className="flex-1 w-full max-w-6xl mx-auto px-4 sm:px-6 py-6">{children}</main>
    </div>
  );
}
